import { parseDateValue } from '@ben/common/date';
import Checkbox from '@ben/orion/checkbox';
import DateInput from '@ben/orion/date-input';
import { TypographyVariant } from '@ben/orion/typography';
import type {
  RenewalConfig,
  RenewalConfigFormErrors,
} from '@ben/shared/types/columbia';
import type {
  DateInputRangeValue,
  DateInputValue,
} from '@ben/orion/date-input';
import { addDays, format } from 'date-fns';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';

// 1 day in future is soonest possible schedule date
const MIN_DATE = addDays(Date.now(), 1);

interface RenewalWindowFormProps {
  formErrors: RenewalConfigFormErrors;
  formValues: RenewalConfig;
  setFormValues: (values: RenewalConfig) => void;
}

const RenewalWindowForm = ({
  formErrors,
  formValues,
  setFormValues,
}: RenewalWindowFormProps) => {
  const {
    scheduledRenewalDate,
    renewalWindowEndDate,
    renewalWindowStartDate,
    hasConfigWindow,
    hasOptInWindow,
    hasOptOutWindow,
  } = formValues;

  return (
    <>
      <DateInput
        label="Renewal date"
        width="100%"
        onChange={(value: DateInputValue) => {
          setFormValues({
            ...formValues,
            scheduledRenewalDate: value ? format(value, 'yyyy-MM-dd') : '',
          });
        }}
        value={
          scheduledRenewalDate ? parseDateValue(scheduledRenewalDate) : null
        }
        errorMessage={
          formErrors.scheduledRenewalDateError
            ? fieldCannotBeBlankErrorMessage
            : ''
        }
        minDate={
          renewalWindowEndDate ? parseDateValue(renewalWindowEndDate) : MIN_DATE
        }
      />
      <DateInput<'range'>
        label="Renewal window"
        description="The end date of the renewal window is not inclusive."
        width="100%"
        type="range"
        value={
          renewalWindowStartDate && renewalWindowEndDate
            ? ([
                parseDateValue(renewalWindowStartDate),
                parseDateValue(renewalWindowEndDate),
              ] as DateInputRangeValue)
            : undefined
        }
        onChange={([startDate, endDate]: DateInputRangeValue) => {
          setFormValues({
            ...formValues,
            renewalWindowStartDate: startDate
              ? format(startDate, 'yyyy-MM-dd')
              : undefined,
            renewalWindowEndDate: endDate
              ? format(endDate, 'yyyy-MM-dd')
              : undefined,
            scheduledRenewalDate:
              endDate &&
              scheduledRenewalDate &&
              parseDateValue(scheduledRenewalDate) < endDate
                ? ''
                : scheduledRenewalDate,
          });
        }}
        errorMessage={
          formErrors.renewalWindowDateError
            ? fieldCannotBeBlankErrorMessage
            : ''
        }
        minDate={MIN_DATE}
      />
      <CheckboxContainer>
        <Checkbox
          label="Is config window"
          typographyVariant={TypographyVariant.bodyMd1}
          value={hasConfigWindow}
          onChange={(hasConfigWindow) =>
            setFormValues({ ...formValues, hasConfigWindow })
          }
        />
        <Checkbox
          label="Is opt-in window"
          typographyVariant={TypographyVariant.bodyMd1}
          value={hasOptInWindow}
          onChange={(hasOptInWindow) =>
            setFormValues({ ...formValues, hasOptInWindow })
          }
        />
        <Checkbox
          label="Is opt-out window"
          typographyVariant={TypographyVariant.bodyMd1}
          value={hasOptOutWindow}
          onChange={(hasOptOutWindow) =>
            setFormValues({ ...formValues, hasOptOutWindow })
          }
        />
      </CheckboxContainer>
    </>
  );
};

export default RenewalWindowForm;

const CheckboxContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(1.5)};
  `}
`;
